/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from 'app/routes';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';

/**
 * Logout.
 */

function logout(client: AxiosInstance): Promise<void> {
  return client.post(resolveAppUrl('logout')).then(({ data }) => data);
}

/**
 * Export `useLogout`.
 */

export function useLogout() {
  const client = useRequestProvider();
  const navigate = useNavigate();
  const { success } = useToast();
  const [translate] = useTranslation();

  return useMutation({
    mutationFn: async () => {
      // Best-effort server logout, but always clear the local session so the
      // user is signed out even when the API is unreachable.
      try {
        await logout(client);
      } catch (error) {
        console.warn('Logout request failed; clearing local session anyway.', error);
      }

      cookieStorage.remove('user', { path: '/' });

      navigate(appRoutes.home);

      // The ToastProvider lives at the app root, so the toast survives the
      // navigation away from the dashboard.
      success({ message: translate('menu.loggedOut') });
    }
  });
}
